import { isValidLatLng } from './constants'

/**
 * Map bounds builders shared by the imperative `useFitBoundsLogic` (manual
 * "Fit Bounds" button) and the effect-based `useFitBoundsWhenReady` (auto-fit
 * after RTKQ + WebGL projection are ready). Both call sites need the same
 * coordinate filtering — see `isValidLatLng` in `./constants` for the (0, 0)
 * rationale.
 */

/**
 * Approximate (lat-only) projection — 1° lat ≈ 111.32 km. Mutates `bounds`.
 * Used to expand bounds by a radius around a center point so the resulting
 * fit shows the whole circle.
 */
export const extendBoundsForRadius = (bounds, latitude, longitude, radius) => {
  const latDelta = radius / 111320
  const lngDelta = radius / (111320 * Math.cos((latitude * Math.PI) / 180))
  bounds.extend({ lat: latitude + latDelta, lng: longitude + lngDelta })
  bounds.extend({ lat: latitude - latDelta, lng: longitude - lngDelta })
}

/**
 * Builds a `LatLngBounds` from a customer center + an array of marker objects
 * + an optional radius. Returns metadata the caller uses to decide between
 * `fitBounds`, a `setCenter` + `setZoom` fallback, or bailing entirely.
 *
 * @param {Object} args
 * @param {Object|null} args.mapsLib - The `google.maps` core library (from `useMapsLibrary('core')`), null until it loads.
 * @param {Array}  [args.data]  - Marker source. Each item may use `latitude/longitude` OR `lat/lng`.
 * @param {number} [args.latitude]
 * @param {number} [args.longitude]
 * @param {number} [args.radius] - Meters. Only applied when center is valid.
 * @returns {{ bounds: google.maps.LatLngBounds, hasMarkers: boolean, centerValid: boolean } | null}
 *   Returns null when `mapsLib` isn't available yet (caller should bail).
 */
export const buildMapFitBounds = ({ mapsLib, data, latitude, longitude, radius }) => {
  if (!mapsLib) return null
  const bounds = new mapsLib.LatLngBounds()
  let hasMarkers = false
  const centerValid = isValidLatLng(latitude, longitude)

  if (centerValid) bounds.extend({ lat: latitude, lng: longitude })

  if (Array.isArray(data)) {
    data.forEach(item => {
      const lat = item.latitude ?? item.lat
      const lng = item.longitude ?? item.lng
      if (isValidLatLng(lat, lng)) {
        bounds.extend({ lat, lng })
        hasMarkers = true
      }
    })
  }

  if (radius && centerValid) extendBoundsForRadius(bounds, latitude, longitude, radius)

  return { bounds, hasMarkers, centerValid }
}
